import React, {useState} from "react";
import {KatButton, KatModal} from "@amzn/katal-react";
import CustomDropdown from "src/app/component/common/CustomDropdown";

interface DropdownOption {
    value: string;
    title: string;
}

interface LighthouseOffboardModalProps {
    visible: boolean;
    reasonCodes: DropdownOption[];
    /** Root cause options for this enforcement, from its ROOT_CAUSES config. */
    rootCauses?: DropdownOption[];
    /** Enforcement the case belongs to. Only ODQ and Reliability change Root Cause handling. */
    caseSubType?: string;
    onSubmit: (reasonCode: string, rootCause: string) => void;
    onClose: () => void;
}

/**
 * Offboard Decision popup for the Lighthouse flow. Reason code is always mandatory.
 *
 * Root Cause follows the classic AE modal for two enforcements only: required for ODQ, and not
 * rendered for Reliability. Every other enforcement (OPF, SentinelVerification, BWND, RCA, ...)
 * keeps its previous behaviour — Root Cause rendered, disabled when it has no options, optional —
 * so their offboard flow is unchanged.
 */
const LighthouseOffboardModal: React.FC<LighthouseOffboardModalProps> = (
    {visible, reasonCodes, rootCauses = [], caseSubType, onSubmit, onClose}
) => {
    const [reasonCode, setReasonCode] = useState("");
    const [rootCause, setRootCause] = useState("");

    const resetForm = () => {
        setReasonCode("");
        setRootCause("");
    };

    const handleClose = () => {
        onClose();
        resetForm();
    };

    const handleSubmit = () => {
        onSubmit(reasonCode, rootCause);
        resetForm();
    };

    const isODQ = caseSubType === 'ODQ';
    const isReliability = caseSubType === 'Reliability';

    // ODQ must record a root cause, matching AE. Guarded on options existing so a missing
    // ROOT_CAUSES row cannot leave an auditor unable to offboard at all.
    const requireRootCause = isODQ && rootCauses.length > 0;

    // Unchanged (`!!reasonCode`) for every enforcement except ODQ.
    const isValid = requireRootCause
        ? !!reasonCode && !!rootCause
        : !!reasonCode;

    // AE omits Root Cause for Reliability. Every other enforcement keeps rendering it as before.
    const showRootCause = !isReliability;

    return (
        <KatModal visible={visible} onClose={handleClose}>
            <span slot="title">Offboard Decision</span>
            <div className="odq-exempt-modal">
                <div className="odq-exempt-modal__field">
                    <CustomDropdown
                        label="Reason Code"
                        placeholder="--Select Reason Code--"
                        options={reasonCodes}
                        value={reasonCode}
                        onChange={setReasonCode}
                        required
                        testId="lighthouse-offboard-reason-select"
                    />
                </div>

                {showRootCause && (
                    <div className="odq-exempt-modal__field">
                        <CustomDropdown
                            label="Root Cause"
                            placeholder="--Select Root Cause--"
                            options={rootCauses}
                            value={rootCause}
                            onChange={setRootCause}
                            disabled={rootCauses.length === 0}
                            required={requireRootCause}
                            testId="lighthouse-offboard-rootcause-select"
                        />
                    </div>
                )}
            </div>
            <div slot="footer" className="kat-group-horizontal">
                <KatButton variant="secondary" label="Cancel" onClick={handleClose}
                           data-testid="lighthouse-offboard-cancel-btn" />
                <KatButton variant="primary" label="Offboard"
                           disabled={!isValid}
                           onClick={handleSubmit}
                           data-testid="lighthouse-offboard-submit-btn" />
            </div>
        </KatModal>
    );
};

export default LighthouseOffboardModal;
